import {
  Button,
  createStyles,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  withStyles,
  type WithStyles,
} from "@material-ui/core";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { Trans, useTranslations } from "adapters/i18n";
import SelectDropdown from "components/SelectDropdown";
import { DATE_PICKER_MASK } from "constants/global";
import { Show, TextLabel } from "features/shared/components";
import { DatePicker } from "material-ui-pickers";
import React, { type HTMLAttributes } from "react";
import { DATE_FORMATS } from "shared/dateUtils";
import type {
  EnrichedAnimalCategory,
  EnrichedHerdRecord,
  OperationType,
} from "shared/types";
import { useMovementsForm } from "../hooks/useMovementsForm";
import { useMovementsFormOptions } from "../hooks/useMovementsFormOptions";
import type { MovementFormState } from "../movements.types";
import { RecordType } from "../utils/movements.utils";

type MovementsFormProps = {
  categories: EnrichedAnimalCategory[];
  onCancel: () => void;
  onSubmit: (formState: MovementFormState) => void;
  operationTypes: OperationType[];
  selectedHerdRecord: EnrichedHerdRecord | null;
} & WithStyles<typeof styles>;

function MovementsForm({
  categories,
  classes,
  onCancel,
  onSubmit,
  operationTypes,
  selectedHerdRecord,
}: MovementsFormProps) {
  const { t } = useTranslations();

  const {
    canSubmitForm,
    formState,
    handleChangeCategory,
    handleChangeCarcassYield,
    handleChangeOperationDate,
    handleChangeOperationType,
    handleChangeQuantity,
    handleChangeRecordType,
    handleChangeWeight,
    handleResetForm,
    handleSubmitForm,
  } = useMovementsForm({ onSubmit, selectedHerdRecord });

  const { categoryOptions, operationTypeOptions, recordTypeOptions } =
    useMovementsFormOptions({
      categories,
      operationTypes,
      selectedRecordType: formState.recordTypeId.value,
    });

  const handleCancelClick = () => {
    handleResetForm();
    onCancel();
  };

  const isEditing = selectedHerdRecord != null;
  const headerText = isEditing
    ? t("movements:edit-movement")
    : t("movements:new-movement");

  const operationDateLabelMap: Record<RecordType, string> = {
    [RecordType.Entry]: t("movements:entry-date").toUpperCase(),
    [RecordType.Exit]: t("movements:exit-date").toUpperCase(),
  };

  return (
    <Paper className={classes.paper} square={true}>
      <Grid item sm={12} xs={12}>
        <Typography className={classes.formHeader} gutterBottom={true}>
          {headerText}
        </Typography>
      </Grid>

      <Grid item sm={12} xs={12}>
        <Typography className={classes.subHeader} gutterBottom={true}>
          {t("common:general-information")}
        </Typography>
      </Grid>

      <Grid item sm={12} xs={12} className={classes.fieldsWrapper}>
        <SelectDropdown
          className={classes.selectInput}
          data-testid="category-select"
          error={formState.animalCategoryId.error != null}
          label={{
            text: t("animalCategory:animal-category").toUpperCase(),
            required: true,
          }}
          onChange={handleChangeCategory}
          options={categoryOptions}
          value={formState.animalCategoryId.value}
        />
        <SelectDropdown
          className={classes.selectInput}
          data-testid="record-type-select"
          error={formState.recordTypeId.error != null}
          label={{
            text: t("movements:movement").toUpperCase(),
            required: true,
          }}
          onChange={handleChangeRecordType}
          options={recordTypeOptions}
          renderOptionComponent={CustomRecordOption}
          value={formState.recordTypeId.value}
        />
        <TextField
          data-testid="quantity-input"
          error={formState.quantity.error != null}
          InputLabelProps={{ className: classes.inputLabel }}
          inputProps={{ className: classes.textInput }}
          label={t("common:amount").toUpperCase()}
          onChange={handleChangeQuantity}
          required={true}
          value={formState.quantity.value ?? ""}
        />

        <DatePicker
          className={classes.pickerInput}
          data-testid="operation-date-picker"
          disableFuture={true}
          disableOpenOnEnter={true}
          format={DATE_FORMATS["DD/MM/YYYY"]}
          InputLabelProps={{ className: classes.inputLabel }}
          invalidDateMessage={t("common:validation.invalid-date-format")}
          keyboard={true}
          label={operationDateLabelMap[formState.recordTypeId.value]}
          mask={(value) => (value ? DATE_PICKER_MASK : [])}
          maxDateMessage={t("common:validation.max-date")}
          onChange={handleChangeOperationDate}
          placeholder="dd/mm/aaaa"
          required={true}
          style={{ padding: "5px 0" }}
          value={formState.operationDate.value}
        />
        <SelectDropdown
          className={classes.selectInput}
          data-testid="operation-type-select"
          error={formState.operationTypeId.error != null}
          label={{
            text: t("movements:movement-type-short").toUpperCase(),
            required: true,
          }}
          onChange={handleChangeOperationType}
          options={operationTypeOptions}
          value={formState.operationTypeId.value}
        />
        <TextField
          data-testid="weight-input"
          error={formState.weight.error != null}
          InputLabelProps={{ className: classes.inputLabel }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {t("common:measure.KG")}
              </InputAdornment>
            ),
          }}
          inputProps={{ className: classes.textInput }}
          label={t("livestock:measured-weight").toUpperCase()}
          onChange={handleChangeWeight}
          required={true}
          value={formState.weight.value ?? ""}
        />

        <Show when={formState.recordTypeId.value === RecordType.Exit}>
          <TextField
            data-testid="carcass-yield-input"
            error={formState.carcassYield.error != null}
            helperText={formState.carcassYield.error}
            InputLabelProps={{ className: classes.inputLabel }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{ className: classes.textInput }}
            label={t("livestock:carcass-yield").toUpperCase()}
            onChange={handleChangeCarcassYield}
            value={formState.carcassYield.value ?? ""}
          />
        </Show>
      </Grid>

      <Grid item sm={12} xs={12} className={classes.bottomContainer}>
        <Show when={!canSubmitForm}>
          <TextLabel
            color="error"
            size={12}
            label={
              <span>
                <Trans
                  i18nKey="common:validation.required-fields"
                  components={{
                    bold: <span className={classes.requiredHighlight} />,
                  }}
                />
              </span>
            }
          />
        </Show>

        <FormActions
          canSubmit={canSubmitForm}
          classes={classes}
          isEditing={isEditing}
          onCancel={handleCancelClick}
          onSubmit={handleSubmitForm}
        />
      </Grid>
    </Paper>
  );
}

function CustomRecordOption(
  props: HTMLAttributes<HTMLLIElement>,
  option: { label: string; value: RecordType }
) {
  return (
    <li {...props} style={{ display: "flex" }}>
      <FiberManualRecordIcon
        style={{
          color: option.value === RecordType.Entry ? "#4fd26b" : "#e98e4f",
          width: "20px",
          height: "20px",
          marginRight: "5px",
        }}
      />
      {option.label}
    </li>
  );
}

type FormActionsProps = {
  canSubmit: boolean;
  isEditing: boolean;
  onCancel: () => void;
  onSubmit: () => void;
} & WithStyles<typeof styles>;

function FormActions({
  canSubmit,
  classes,
  isEditing,
  onCancel,
  onSubmit,
}: FormActionsProps) {
  const { t } = useTranslations();

  const submitButtonText = isEditing
    ? t("common:actions.save")
    : t("common:actions.add");

  return (
    <div className={classes.formActionsContainer}>
      <Button color="primary" data-testid="cancel-button" onClick={onCancel}>
        {t("common:actions.cancel")}
      </Button>
      <Button
        color="primary"
        data-testid="submit-button"
        disabled={!canSubmit}
        onClick={onSubmit}
        variant="contained"
      >
        {submitButtonText}
      </Button>
    </div>
  );
}

const styles = () =>
  createStyles({
    paper: {
      borderRadius: "16px",
      marginTop: "10px",
      padding: "20px",
      width: "100%",
    },
    formHeader: {
      color: "#333333",
      fontSize: "20px",
      fontStretch: "medium",
      fontStyle: "medium",
      fontWeight: "bold",
      letterSpacing: "medium",
      lineHeight: "medium",
      marginTop: "16px",
    },
    subHeader: {
      fontFamily: "Roboto",
      fontSize: "14px",
      letterSpacing: "-0.03px",
      color: "#535353",
      fontWeight: "bold",
      marginTop: "16px",
      marginBottom: "12px",
    },
    fieldsWrapper: {
      display: "flex",
      flexDirection: "row",
      gap: "32px",
      flexWrap: "wrap",
    },
    selectInput: {
      width: "220px !important",
    },
    pickerInput: {
      width: "180px",
      opacity: "0.9",
      fontSize: "8px",
    },
    textInput: {
      paddingBottom: "12px",
    },
    inputLabel: {
      width: "max-content",
    },
    bottomContainer: {
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: "36px",
    },
    formActionsContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: "20px",
      marginLeft: "auto",
    },
    requiredHighlight: {
      color: "#666666",
      verticalAlign: "middle",
      fontSize: "21px",
    },
  });

export default withStyles(styles)(MovementsForm);
